import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactionTest from '../tests/ReactionTest';
import AimTest from '../tests/AimTest';
import TrackingTest from '../tests/TrackingTest';
import MemoryTest from '../tests/MemoryTest';
import FlickerTest from '../tests/FlickerTest';
import SequencingTest from '../tests/SequencingTest';
import ComposureTest from '../tests/ComposureTest';
import TiltTest from '../tests/TiltTest';
import { getPracticeTrials } from '../services/api';

const PRACTICE_MODULES = [
  {
    id: 'reaction',
    label: 'Reaction & Precision',
    color: '#00d4ff',
    desc: 'Simple, choice, and go/no-go reaction time tasks.',
    svg: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
  },
  {
    id: 'aim',
    label: 'Aim Precision',
    color: '#ef4444',
    desc: '25 randomized targets — click as fast and accurately as you can.',
    svg: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3" />
        <line x1="12" y1="2" x2="12" y2="6" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="2" y1="12" x2="6" y2="12" />
        <line x1="18" y1="12" x2="22" y2="12" />
      </svg>
    ),
  },
  {
    id: 'tracking',
    label: 'Target Tracking',
    color: '#10b981',
    desc: 'Keep your cursor inside a moving target for 30 seconds.',
    svg: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M21 12a9 9 0 1 1-6.22-8.56" strokeDasharray="3 2.2" />
        <circle cx="18" cy="6" r="2.2" fill="currentColor" fillOpacity="0.2" />
        <path d="M12 12 L18 6" strokeWidth="1.2" opacity="0.5" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'memory',
    label: 'Working Memory',
    color: '#a855f7',
    desc: 'Grid recall — watch cells flash in sequence, then click them back in the same order.',
    svg: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" opacity="0.45" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" opacity="0.45" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
        <path d="M10 6.5h4M17.5 10v4M14 17.5h-4M6.5 14v-4" strokeWidth="1.1" opacity="0.5" strokeDasharray="1.5 1" />
      </svg>
    ),
  },
  {
    id: 'flicker',
    label: 'Flick Speed',
    color: '#f59e0b',
    desc: 'FPS module — click targets before they disappear in 500ms.',
    svg: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="12" cy="12" r="2.5" fill="currentColor" fillOpacity="0.2" />
        <path d="M12 3v3.5M12 17.5v3.5M3 12h3.5M17.5 12H21" />
        <path d="M6.4 6.4l2.3 2.3M15.3 15.3l2.3 2.3M6.4 17.6l2.3-2.3M15.3 8.7l2.3-2.3" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    id: 'sequencing',
    label: 'Target Sequencing',
    color: '#6366f1',
    desc: 'MOBA module — click numbered targets in order before time runs out.',
    svg: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="5" cy="5" r="2.5" />
        <circle cx="19" cy="5" r="2.5" />
        <circle cx="19" cy="19" r="2.5" />
        <circle cx="5" cy="19" r="2.5" />
        <path d="M7.5 5h9M19 7.5v9M16.5 19h-9M5 16.5v-9" strokeWidth="1.2" strokeDasharray="2 1.5" opacity="0.5" />
      </svg>
    ),
  },
  {
    id: 'composure',
    label: 'Composure Under Fire',
    color: '#06b6d4',
    desc: 'Flanker task — identify the center arrow while ignoring surrounding distractors.',
    svg: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12h4M10 12h4M20 12h-2" />
        <path d="M14 9l3 3-3 3" />
        <circle cx="12" cy="12" r="9" strokeOpacity="0.3" />
        <path d="M7 9l-3 3 3 3" strokeOpacity="0.4" />
        <path d="M17 9l3 3-3 3" strokeOpacity="0.4" />
      </svg>
    ),
  },
  {
    id: 'tilt',
    label: 'Tilt Recovery',
    color: '#f43f5e',
    desc: 'Bet Under Pressure — place a risk bet before each round, then respond to a go/no-go stimulus through a failure phase and recovery.',
    svg: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 17l4-8 4 5 3-3 4 6" />
        <circle cx="19" cy="6" r="2" fill="currentColor" fillOpacity="0.2" />
        <path d="M19 4v4M17 6h4" strokeWidth="1.2" />
      </svg>
    ),
  },
];

// ── Personal Best tracking (localStorage) ────────────────────────────────────
const PB_HEADLINES = {
  reaction:   { label: 'Avg reaction time',            betterWhen: 'lower'  },
  aim:        { label: 'Targets hit',                  betterWhen: 'higher',
    transform: (v) => { const m = v.match(/(\d+)\s*\/\s*(\d+)/); return m ? Math.round((parseInt(m[1]) / parseInt(m[2])) * 100) : null; },
    display:   (v) => { const m = v.match(/(\d+)\s*\/\s*(\d+)/); return m ? `${Math.round((parseInt(m[1]) / parseInt(m[2])) * 100)}%` : v; },
  },
  tracking:   { label: 'Time on target',               betterWhen: 'higher' },
  memory:     { label: 'Perfect rounds',               betterWhen: 'higher',
    transform: (v) => { const m = v.match(/(\d+)\s*\/\s*(\d+)/); return m ? Math.round((parseInt(m[1]) / parseInt(m[2])) * 100) : null; },
    display:   (v) => { const m = v.match(/(\d+)\s*\/\s*(\d+)/); return m ? `${Math.round((parseInt(m[1]) / parseInt(m[2])) * 100)}%` : v; },
  },
  flicker:    { label: 'Hit rate',                     betterWhen: 'higher' },
  sequencing: { label: 'Completion rate',              betterWhen: 'higher' },
  composure:  { label: 'Composure retained',           betterWhen: 'higher' },
  tilt:       { label: 'Rational bets under pressure', betterWhen: 'higher' },
};

function parsePBValue(rawValue, config) {
  if (config.transform) return config.transform(rawValue);
  const n = parseFloat(String(rawValue).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? null : n;
}
function getPB(moduleId) {
  try { return JSON.parse(localStorage.getItem(`nr_pb_${moduleId}`)) || null; } catch { return null; }
}
function computeAndSavePB(moduleId, stats) {
  const cfg = PB_HEADLINES[moduleId];
  if (!cfg) return { isNewPB: false, pb: null };
  const stat = stats.find((s) => s.label === cfg.label);
  if (!stat) return { isNewPB: false, pb: null };
  const current = parsePBValue(stat.value, cfg);
  if (current == null) return { isNewPB: false, pb: null };
  const stored = getPB(moduleId);
  const isNewPB = !stored || (cfg.betterWhen === 'lower' ? current < stored.value : current > stored.value);
  if (isNewPB) {
    const pb = { value: current, display: cfg.display ? cfg.display(stat.value) : stat.value, label: cfg.label, betterWhen: cfg.betterWhen };
    try { localStorage.setItem(`nr_pb_${moduleId}`, JSON.stringify(pb)); } catch {}
    return { isNewPB: true, pb };
  }
  return { isNewPB: false, pb: stored };
}

function computePracticeStats(module, results) {
  if (!results || results.length === 0) return [];

  if (module === 'reaction') {
    const simple = results.filter((r) => r.type === 'simple');
    const simpleHits = simple.filter((r) => r.correct && r.rt);
    const avgRT = simpleHits.length
      ? Math.round(simpleHits.reduce((s, r) => s + r.rt, 0) / simpleHits.length)
      : null;
    const choice = results.filter((r) => r.type === 'choice');
    const choiceAcc = choice.length
      ? Math.round((choice.filter((r) => r.correct).length / choice.length) * 100)
      : null;
    const gonogo = results.filter((r) => r.type === 'gonogo');
    const falseAlarms = gonogo.filter((r) => !r.isGo && r.correct === false).length;
    return [
      avgRT !== null && { label: 'Avg reaction time', value: `${avgRT}ms` },
      choiceAcc !== null && { label: 'Choice accuracy', value: `${choiceAcc}%` },
      gonogo.length > 0 && { label: 'False alarms (go/no-go)', value: falseAlarms },
    ].filter(Boolean);
  }

  if (module === 'aim') {
    const hits = results.filter((r) => r.correct).length;
    const hitRTs = results.filter((r) => r.correct && r.rt);
    const avgRT = hitRTs.length
      ? Math.round(hitRTs.reduce((s, r) => s + r.rt, 0) / hitRTs.length)
      : null;
    return [
      { label: 'Targets hit', value: `${hits} / ${results.length}` },
      avgRT !== null && { label: 'Avg click time', value: `${avgRT}ms` },
    ].filter(Boolean);
  }

  if (module === 'tracking') {
    const r = results[0] || {};
    const pct = Math.round((r.timeOnTarget || 0) * 100);
    const dist = r.avgNormDistance != null ? r.avgNormDistance.toFixed(2) : '—';
    return [
      { label: 'Time on target', value: `${pct}%` },
      { label: 'Avg distance (normalized)', value: dist },
    ];
  }

  if (module === 'memory') {
    const totalCells = results.reduce((s, r) => s + (r.sequenceLength || 0), 0);
    const correctCells = results.reduce((s, r) => s + (r.correctCount || 0), 0);
    const perfectRounds = results.filter((r) => r.correct).length;
    return [
      totalCells > 0 && { label: 'Cells recalled', value: `${correctCells} / ${totalCells}` },
      { label: 'Perfect rounds', value: `${perfectRounds} / ${results.length}` },
    ].filter(Boolean);
  }

  if (module === 'flicker') {
    const hits = results.filter((r) => r.correct);
    const hitRTs = hits.filter((r) => r.rt !== null).map((r) => r.rt);
    const avgRT = hitRTs.length
      ? Math.round(hitRTs.reduce((s, r) => s + r, 0) / hitRTs.length)
      : null;
    return [
      { label: 'Targets hit', value: `${hits.length} / ${results.length}` },
      { label: 'Hit rate', value: `${Math.round((hits.length / (results.length || 1)) * 100)}%` },
      avgRT !== null && { label: 'Avg reaction time', value: `${avgRT}ms` },
    ].filter(Boolean);
  }

  if (module === 'sequencing') {
    const completed = results.filter((r) => r.completed).length;
    const meanRTs = results.filter((r) => r.completed && r.meanRT).map((r) => r.meanRT);
    const avgRT = meanRTs.length
      ? Math.round(meanRTs.reduce((s, r) => s + r, 0) / meanRTs.length)
      : null;
    return [
      { label: 'Rounds completed', value: `${completed} / ${results.length}` },
      { label: 'Completion rate', value: `${Math.round((completed / (results.length || 1)) * 100)}%` },
      avgRT !== null && { label: 'Avg inter-click time', value: `${avgRT}ms` },
    ].filter(Boolean);
  }

  if (module === 'composure') {
    const baseline = results.filter((r) => r.phase === 'baseline');
    const distraction = results.filter((r) => r.phase === 'distraction');
    const baselineAcc = baseline.length
      ? Math.round((baseline.filter((r) => r.correct).length / baseline.length) * 100)
      : null;
    const distractionAcc = distraction.length
      ? Math.round((distraction.filter((r) => r.correct).length / distraction.length) * 100)
      : null;
    return [
      baselineAcc !== null && { label: 'Baseline accuracy', value: `${baselineAcc}%` },
      distractionAcc !== null && { label: 'Accuracy under distraction', value: `${distractionAcc}%` },
      baselineAcc !== null && distractionAcc !== null && {
        label: 'Composure retained',
        value: baselineAcc > 0 ? `${Math.round((distractionAcc / baselineAcc) * 100)}%` : '—',
      },
    ].filter(Boolean);
  }

  if (module === 'tilt') {
    const bettingTrials = results.filter((r) => r.prevOutcome != null);
    const rational = bettingTrials.filter(
      (r) => (r.prevOutcome === 'failure' && r.bet === 'low') || (r.prevOutcome === 'success' && r.bet === 'high')
    );
    const rationalPct = bettingTrials.length
      ? Math.round((rational.length / bettingTrials.length) * 100)
      : null;
    const recovery = results.filter((r) => r.phase === 'recovery');
    const recoveryAcc = recovery.length
      ? Math.round((recovery.filter((r) => r.correct).length / recovery.length) * 100)
      : null;
    return [
      rationalPct !== null && { label: 'Rational bets under pressure', value: `${rationalPct}%` },
      recoveryAcc !== null && { label: 'Recovery phase accuracy', value: `${recoveryAcc}%` },
    ].filter(Boolean);
  }

  return [];
}

export default function PracticeRunner() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('picking'); // picking | loading | running | done
  const [selectedModule, setSelectedModule] = useState(null);
  const [trials, setTrials] = useState(null);
  const [stats, setStats] = useState([]);
  const [pbResult, setPbResult] = useState({ isNewPB: false, pb: null });
  const [error, setError] = useState('');

  React.useEffect(() => {
    document.title = 'Practice Mode | NeuroRank';
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []);

  // ESC key exits back to module picker from anywhere in practice
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && (phase === 'running' || phase === 'done')) {
        setTrials(null);
        setStats([]);
        setSelectedModule(null);
        setPhase('picking');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase]);

  const selectModule = async (moduleId) => {
    setSelectedModule(moduleId);
    setPhase('loading');
    setError('');
    import('../services/track').then(({ track }) => track('practice_start', { module: moduleId }));
    try {
      const { trials: t } = await getPracticeTrials(moduleId);
      if (!Array.isArray(t) || t.length === 0) {
        throw new Error('Server returned no trials for this module.');
      }
      setTrials(t);
      setPhase('running');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load module.';
      setError(msg);
      setSelectedModule(null);
      setPhase('picking');
    }
  };

  const handleComplete = (results) => {
    const computedStats = computePracticeStats(selectedModule, results);
    const pbRes = computeAndSavePB(selectedModule, computedStats);
    setStats(computedStats);
    setPbResult(pbRes);
    setPhase('done');
  };

  const tryAgain = () => {
    setTrials(null);
    setStats([]);
    setPbResult({ isNewPB: false, pb: null });
    setSelectedModule(null);
    setPhase('picking');
  };

  const mod = PRACTICE_MODULES.find((m) => m.id === selectedModule);

  // --- Module picker ---
  if (phase === 'picking') {
    return (
      <div className="container" style={{ paddingTop: 48, paddingBottom: 80 }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <h2 style={{ marginBottom: 8 }}>Practice Mode</h2>
          <p className="text-muted" style={{ marginBottom: 32, fontSize: 14 }}>
            Run any single module solo — no session created, no profile saved.
          </p>

          {error && (
            <p style={{ color: 'var(--danger)', marginBottom: 20, fontSize: 14 }}>{error}</p>
          )}

          <div className="practice-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
            {PRACTICE_MODULES.map((m, i) => (
              <motion.button
                key={m.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
                onClick={() => selectModule(m.id)}
                style={{
                  background: 'var(--bg-card)',
                  border: 'none',
                  padding: '28px 24px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-card)'; }}
              >
                {/* Icon */}
                <div style={{
                  width: 48, height: 48, borderRadius: 12, marginBottom: 20,
                  background: `${m.color}18`,
                  border: `1px solid ${m.color}28`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: m.color, flexShrink: 0,
                }}>
                  {m.svg}
                </div>
                {/* Label */}
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', marginBottom: 8, letterSpacing: '-0.02em' }}>
                  {m.label}
                </div>
                {/* Desc */}
                <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0, flex: 1 }}>
                  {m.desc}
                </p>
                {/* PB badge */}
                {(() => { const pb = getPB(m.id); return pb ? (
                  <div style={{ marginTop: 12, fontSize: 11, color: '#f59e0b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span>🏆</span> PB: {pb.display}
                  </div>
                ) : null; })()}
                {/* Footer */}
                <div style={{
                  marginTop: 20, paddingTop: 14,
                  borderTop: '1px solid var(--border)',
                  fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: m.color,
                }}>
                  Start Practice →
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  // --- Loading ---
  if (phase === 'loading') {
    return (
      <div className="container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <p className="text-muted">Loading module...</p>
      </div>
    );
  }

  // --- Running ---
  if (phase === 'running' && trials) {
    return (
      <div className="container" style={{ paddingTop: 32, paddingBottom: 48, userSelect: 'none' }}>
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="text-muted" style={{ fontSize: 13 }}>
            Practice — {mod?.label}
          </span>
          <button
            onClick={tryAgain}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '7px 16px',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              cursor: 'default', // always visible even when body cursor is none
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              zIndex: 10,
              position: 'relative',
            }}
          >
            ← Exit
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400 }}>[Esc]</span>
          </button>
        </div>

        {selectedModule === 'reaction' && <ReactionTest icon={mod?.svg} trials={trials} onComplete={handleComplete} />}
        {selectedModule === 'aim' && <AimTest icon={mod?.svg} trials={trials} onComplete={handleComplete} />}
        {selectedModule === 'tracking' && <TrackingTest icon={mod?.svg} trials={trials} onComplete={handleComplete} />}
        {selectedModule === 'memory' && <MemoryTest icon={mod?.svg} trials={trials} onComplete={handleComplete} />}
        {selectedModule === 'flicker' && <FlickerTest icon={mod?.svg} trials={trials} onComplete={handleComplete} />}
        {selectedModule === 'sequencing' && <SequencingTest icon={mod?.svg} trials={trials} onComplete={handleComplete} />}
        {selectedModule === 'composure' && <ComposureTest icon={mod?.svg} trials={trials} onComplete={handleComplete} />}
        {selectedModule === 'tilt' && <TiltTest icon={mod?.svg} trials={trials} onComplete={handleComplete} />}
      </div>
    );
  }

  // --- Done ---
  if (phase === 'done') {
    return (
      <div className="container" style={{ paddingTop: 64, paddingBottom: 80 }}>
        <motion.div
          style={{ maxWidth: 520, margin: '0 auto' }}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          {/* Header card */}
          <div className="card" style={{
            marginBottom: 16,
            borderTop: `3px solid ${mod?.color || 'var(--accent)'}`,
            textAlign: 'center',
            padding: '32px 28px 24px',
          }}>
            {/* Module icon */}
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: `${mod?.color || 'var(--accent)'}18`,
              border: `1px solid ${mod?.color || 'var(--accent)'}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: mod?.color || 'var(--accent)',
              margin: '0 auto 20px',
            }}>
              {mod?.svg}
            </div>
            <p style={{ fontSize: 11, letterSpacing: 3, color: mod?.color || 'var(--accent)', fontWeight: 700, marginBottom: 8 }}>
              PRACTICE COMPLETE
            </p>
            <h2 style={{ margin: '0 0 4px', fontSize: 22 }}>{mod?.label}</h2>
            <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>Raw results — no profile saved</p>
          </div>

          {/* PB banner */}
          {pbResult.isNewPB && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              style={{
                marginBottom: 16, padding: '14px 20px',
                borderRadius: 12, border: '1px solid #f59e0b55',
                background: '#f59e0b0d',
                display: 'flex', alignItems: 'center', gap: 12,
              }}
            >
              <span style={{ fontSize: 22 }}>🏆</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#f59e0b' }}>New Personal Best!</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pbResult.pb?.label}: {pbResult.pb?.display}</div>
              </div>
            </motion.div>
          )}
          {!pbResult.isNewPB && pbResult.pb && (
            <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              <span>🏆</span> Personal best: {pbResult.pb.display}
            </div>
          )}

          {/* Stats card */}
          {stats.length > 0 && (
            <div className="card" style={{ marginBottom: 16, padding: '20px 24px' }}>
              <p style={{ fontSize: 11, letterSpacing: 2, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 16 }}>YOUR STATS</p>
              {stats.map(({ label, value }, i) => (
                <div
                  key={label}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: i < stats.length - 1 ? '1px solid var(--border)' : 'none',
                  }}
                >
                  <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{label}</span>
                  <span style={{ fontSize: 17, fontWeight: 800, color: mod?.color || 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                </div>
              ))}
            </div>
          )}

          {/* CTA card */}
          <div className="card" style={{ padding: '20px 24px' }}>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 20, lineHeight: 1.65 }}>
              These are raw stats only. For a full cognitive profile, archetype classification, and percentile ranking — take the complete combine.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn btn--ghost" onClick={tryAgain} style={{ flex: 1, minWidth: 140 }}>
                ← Try Another Module
              </button>
              <button className="btn btn--primary" onClick={() => navigate('/')} style={{ flex: 1, minWidth: 140 }}>
                Full Combine →
              </button>
            </div>
            {pbResult.isNewPB && (
              <button
                className="btn btn--ghost"
                style={{ marginTop: 10, width: '100%', borderColor: '#1d9bf044', color: '#1d9bf0' }}
                onClick={() => {
                  const text = `Just set a new personal best in ${mod?.label} practice on @NeuroRank — ${pbResult.pb?.display}. Think you can beat it?`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent('https://neurorank.app/practice')}`, '_blank', 'noopener');
                }}
              >
                Share New PB on X
              </button>
            )}
            <p className="text-muted" style={{ fontSize: 11, marginTop: 14, textAlign: 'center' }}>
              Press <span style={{ color: 'var(--text-secondary)' }}>Esc</span> anytime to return to module picker
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
}
